import { useEffect, useId, useState } from "react";
import { api } from "../api";
import "./analytics.css";

type Feedback = { feedback: string; reviewedAt: number | null };
type FeedbackState = { endpoint: string; data?: Feedback; error?: string };

export function LearnerFeedback({ attemptId }: { attemptId: string }) {
  const headingId = useId();
  const endpoint = `/api/attempts/${encodeURIComponent(attemptId)}/feedback`;
  const [state, setState] = useState<FeedbackState | null>(null);
  const [retry, setRetry] = useState(0);
  const current = state?.endpoint === endpoint ? state : null;

  useEffect(() => {
    const controller = new AbortController();
    void api<Feedback>(endpoint, { signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setState({ endpoint, data }); })
      .catch((reason) => {
        if (!controller.signal.aborted) setState({ endpoint, error: reason instanceof Error ? reason.message : "Please try again." });
      });
    return () => controller.abort();
  }, [endpoint, retry]);

  if (!current) return <p className="learner-feedback-status" role="status">Loading teacher feedback…</p>;
  if (current.error) return <div className="learner-feedback-error"><p role="alert">Could not load teacher feedback. {current.error}</p><button type="button" className="secondary-button" onClick={() => { setState(null); setRetry((value) => value + 1); }}>Retry feedback</button></div>;
  if (!current.data?.feedback.trim()) return null;

  const reviewedAt = current.data.reviewedAt === null ? null : new Date(current.data.reviewedAt * 1000);
  const validDate = reviewedAt && Number.isFinite(reviewedAt.getTime()) ? reviewedAt : null;
  return <section className="learner-feedback" aria-labelledby={headingId}>
    <header><h2 id={headingId}>Teacher feedback</h2>{validDate && <time dateTime={validDate.toISOString()}>{validDate.toLocaleDateString(undefined, { dateStyle: "medium" })}</time>}</header>
    <p className="learner-feedback-text">{current.data.feedback}</p>
  </section>;
}
